// One look for every city on the web: a square, full-bleed photo of the city with the country's flag pinned top-left
// as a small rounded badge (a touch bigger than an emoji) — the photo stays the hero, the flag says where it is.
// Photos: Wikipedia/Wikimedia Commons via our cached /travel/photo. Flags: flagcdn.com (public domain).
import React, { useEffect, useState } from "react";
import { travelApi, flagUrl } from "@/lib/travelApi";

const cache = new Map();

/** Photo + flag for a city we only know by name (guides, Kavaya's ideas). Shared across tiles on the page. */
export function useCityPhoto(c) {
  const key = c ? [c.name, c.country, c.iata].join("|") : "";
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!c || !c.name || (c.photo && (c.photo.thumb || c.photo.url) && (c.flag || c.country_code))) return undefined;
    let on = true;
    if (!cache.has(key)) cache.set(key, travelApi.photo({ kind: "city", name: c.name, country: c.country, iata: c.iata }).catch(() => null));
    cache.get(key).then((r) => { if (on) setP(r); });
    return () => { on = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const photo = (c && c.photo && (c.photo.thumb || c.photo.url)) || (p && p.photo && (p.photo.thumb || p.photo.url)) || null;
  const flag = (c && (c.flag || flagUrl(c.country_code))) || (p && p.flag) || null;
  return { photo, flag };
}


export function DiagonalTile({ photo, flag, hue = 210, alt = "", className = "", children }) {
  return (
    <span className={"relative block aspect-square w-full overflow-hidden rounded-2xl text-white " + className}
      style={{ background: "linear-gradient(150deg, hsl(" + hue + " 70% 55%), hsl(" + ((hue + 40) % 360) + " 65% 40%))" }}>
      {photo && <img src={photo} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />}
      {flag && (
        <span className="absolute left-2.5 top-2.5 block h-[22px] w-[31px] overflow-hidden rounded-[6px] ring-2 ring-white/90 shadow-[0_2px_8px_rgba(0,0,0,0.35)]" aria-hidden="true">
          <img src={flag} alt="" loading="lazy" className="h-full w-full object-cover" />
        </span>
      )}
      <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" aria-hidden="true" />
      <span className="absolute inset-x-3 bottom-3">{children}</span>
    </span>
  );
}

/** Square city tile with name, airport code and country. */
export function FlagCityTile({ c, sub, size = "md" }) {
  const { photo, flag } = useCityPhoto(c);
  const big = size === "lg";
  return (
    <DiagonalTile photo={photo} flag={flag} hue={c.hue || 210} alt={c.name}>
      <span className={"block font-serif-display font-bold leading-tight drop-shadow " + (big ? "text-2xl" : "text-lg")}>{c.name}</span>
      <span className="mt-1 flex items-center gap-1.5 text-xs text-white/90">
        {c.iata && <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-bold backdrop-blur-sm">{c.iata}</span>}
        <span className="truncate">{sub || (c.country ? c.country.charAt(0).toUpperCase() + c.country.slice(1) : "")}</span>
      </span>
    </DiagonalTile>
  );
}

export default FlagCityTile;
